import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_KEYS } from '../common/consts';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { AuthPageContainer } from '../auth';
import { Header } from '../common/components/header/Header.component';
import useAuth from '../auth/feature/auth';
import { PricingPage } from '../pricing';
import { SettingsPage } from '../settings';
import { DashboardPage } from '../dashboard';
import { ReviewPage } from '../review';
import { ReviewAdminPage } from '../review/admin';
import { TeamsPage } from '../team';
import { TeamDashboard } from '../team/dashboard';
import { TeamAdminPage } from '../team/admin';
import { JoinTeamPage } from '../team/join';
import { DocsPage } from '../docs';
import { ContactPage } from '../contact';
import { TermsPage } from '../legal/terms';
import { PrivacyPage } from '../legal/privacy';
import { Footer } from '../common/components/footer/Footer.component';
import { CookieConsent } from '../common/components/cookie-consent.component';
import { EnableNotificationsPrompt } from '../common/components/enable-notifications-prompt.component';
import { SiteAdminPage } from '../admin';
import SiteChangelog from '../admin/changelog';
import { InvitePage } from '../invite';
import { MfaApprovePage } from '../auth/forms/mfa-approve.component';
import { CompanyDashboard } from '../company';
import { CompanyBrandingProvider } from '../company/CompanyBrandingProvider';
import { DeveloperPage } from '../developer';
import { OAuthAuthorizePage } from '../oauth';

const LogoPreviewPage = () => (
  <div style={{ fontFamily: 'DM Sans, sans-serif', background: '#F7F7F8', minHeight: '100vh', padding: '48px 32px', color: '#1D1D1E' }}>
    <p style={{ fontFamily: 'Syne, sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#818185', marginBottom: 40 }}>Standby: Logo Concepts</p>

    {/* Helpers */}
    {[
      { label: 'Concept A: Three-dot queue', lightIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><circle cx="12" cy="32" r="9" fill="#0ea5c9"/><circle cx="32" cy="32" r="9" fill="#1D1D1E"/><circle cx="52" cy="32" r="9" fill="#1D1D1E" opacity="0.3"/></svg>, darkIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><circle cx="12" cy="32" r="9" fill="#0ea5c9"/><circle cx="32" cy="32" r="9" fill="white"/><circle cx="52" cy="32" r="9" fill="white" opacity="0.3"/></svg>, icon128: <svg width="128" height="128" viewBox="0 0 64 64" fill="none"><circle cx="12" cy="32" r="9" fill="#0ea5c9"/><circle cx="32" cy="32" r="9" fill="#1D1D1E"/><circle cx="52" cy="32" r="9" fill="#1D1D1E" opacity="0.3"/></svg>, icon32: <svg width="32" height="32" viewBox="0 0 64 64" fill="none"><circle cx="12" cy="32" r="9" fill="#0ea5c9"/><circle cx="32" cy="32" r="9" fill="#1D1D1E"/><circle cx="52" cy="32" r="9" fill="#1D1D1E" opacity="0.3"/></svg>, icon16: <svg width="16" height="16" viewBox="0 0 64 64" fill="none"><circle cx="12" cy="32" r="9" fill="#0ea5c9"/><circle cx="32" cy="32" r="9" fill="#1D1D1E"/><circle cx="52" cy="32" r="9" fill="#1D1D1E" opacity="0.3"/></svg> },
      { label: 'Concept B: S-mark with status dot', lightIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><text x="8" y="50" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="52" fill="#1D1D1E">S</text><circle cx="47" cy="14" r="7" fill="#0ea5c9"/></svg>, darkIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><text x="8" y="50" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="52" fill="white">S</text><circle cx="47" cy="14" r="7" fill="#0ea5c9"/></svg>, icon128: <svg width="128" height="128" viewBox="0 0 64 64" fill="none"><text x="8" y="50" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="52" fill="#1D1D1E">S</text><circle cx="47" cy="14" r="7" fill="#0ea5c9"/></svg>, icon32: <svg width="32" height="32" viewBox="0 0 64 64" fill="none"><text x="8" y="50" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="52" fill="#1D1D1E">S</text><circle cx="47" cy="14" r="7" fill="#0ea5c9"/></svg>, icon16: <svg width="16" height="16" viewBox="0 0 64 64" fill="none"><text x="8" y="50" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="52" fill="#1D1D1E">S</text><circle cx="47" cy="14" r="7" fill="#0ea5c9"/></svg> },
      { label: 'Concept C: Queue stack bars', lightIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="8" y="16" width="48" height="10" rx="5" fill="#0ea5c9"/><rect x="8" y="32" width="36" height="10" rx="5" fill="#1D1D1E"/><rect x="8" y="48" width="24" height="10" rx="5" fill="#1D1D1E" opacity="0.3"/></svg>, darkIcon: <svg width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="8" y="16" width="48" height="10" rx="5" fill="#0ea5c9"/><rect x="8" y="32" width="36" height="10" rx="5" fill="white"/><rect x="8" y="48" width="24" height="10" rx="5" fill="white" opacity="0.3"/></svg>, icon128: <svg width="128" height="128" viewBox="0 0 64 64" fill="none"><rect x="8" y="16" width="48" height="10" rx="5" fill="#0ea5c9"/><rect x="8" y="32" width="36" height="10" rx="5" fill="#1D1D1E"/><rect x="8" y="48" width="24" height="10" rx="5" fill="#1D1D1E" opacity="0.3"/></svg>, icon32: <svg width="32" height="32" viewBox="0 0 64 64" fill="none"><rect x="8" y="16" width="48" height="10" rx="5" fill="#0ea5c9"/><rect x="8" y="32" width="36" height="10" rx="5" fill="#1D1D1E"/><rect x="8" y="48" width="24" height="10" rx="5" fill="#1D1D1E" opacity="0.3"/></svg>, icon16: <svg width="16" height="16" viewBox="0 0 64 64" fill="none"><rect x="8" y="16" width="48" height="10" rx="5" fill="#0ea5c9"/><rect x="8" y="32" width="36" height="10" rx="5" fill="#1D1D1E"/><rect x="8" y="48" width="24" height="10" rx="5" fill="#1D1D1E" opacity="0.3"/></svg> },
    ].map(({ label, lightIcon, darkIcon, icon128, icon32, icon16 }) => (
      <div key={label} style={{ marginBottom: 56 }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: '#BFBFC0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>{label}</p>
        {/* Sizes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
          {[{ el: icon128, lbl: '128px' }, { el: lightIcon, lbl: '64px' }, { el: icon32, lbl: '32px' }, { el: icon16, lbl: '16px' }].map(({ el, lbl }) => (
            <div key={lbl} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ background: 'white', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{el}</div>
              <span style={{ fontSize: 11, color: '#818185' }}>{lbl}</span>
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ background: '#1D1D1E', borderRadius: 16, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{darkIcon}</div>
            <span style={{ fontSize: 11, color: '#818185' }}>On dark</span>
          </div>
        </div>
        {/* Lockups */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ background: 'white', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', padding: '20px 24px', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            {lightIcon}
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '-0.5px', color: '#1D1D1E' }}>Standby</span>
          </div>
          <div style={{ background: '#1D1D1E', borderRadius: 16, padding: '20px 24px', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            {darkIcon}
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '-0.5px', color: 'white' }}>Standby</span>
          </div>
        </div>
        <div style={{ height: 1, background: 'rgba(0,0,0,0.07)', margin: '40px 0 0' }} />
      </div>
    ))}
  </div>
);

const QrRedirectPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = React.useState('Checking in...');
  React.useEffect(() => {
    if (!token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL_DEV || 'http://localhost:4200';
    const base = backendUrl.replace(/\/api\/v1\/?$/, '/api/v1');
    fetch(`${base}/review/qr-checkin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qrToken: token }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) {
          setStatus('error');
          setMessage(data?.message || 'Check-in failed');
          return;
        }
        if (data?.alreadyCheckedIn) {
          setStatus('success');
          setMessage('Already checked in!');
        } else {
          setStatus('success');
          setMessage('Checked in successfully!');
        }
        if (data?.slug) {
          setTimeout(() => navigate(`/review/${data.slug}`, { replace: true }), 1500);
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Check-in failed. Please try again.');
      });
  }, [token]);
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-[12px]">
      {status === 'loading' && <p className="font-body text-[16px] text-[#818185]">Checking in...</p>}
      {status === 'success' && (
        <>
          <div className="w-[48px] h-[48px] rounded-full bg-[#ECFDF5] flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <p className="font-body text-[18px] font-semibold text-base-black dark:text-white">{message}</p>
          <p className="font-body text-[13px] text-[#818185]">Redirecting to queue...</p>
        </>
      )}
      {status === 'error' && (
        <>
          <div className="w-[48px] h-[48px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D8210C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </div>
          <p className="font-body text-[16px] font-medium text-[#D8210C]">{message}</p>
        </>
      )}
    </div>
  );
};

const NotFound = () => (
  <div className="flex flex-col items-center justify-center min-h-[400px]">
    <h1 className="font-display text-[48px] font-bold text-base-black">404</h1>
    <p className="font-body text-[16px] text-gray-900">Page not found</p>
  </div>
);

const HomePage = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  // min-h-full rather than a 400px minimum, so the lockup sits in the middle of
  // the screen instead of near the top with the page empty beneath it. It
  // resolves against main, whose height the shell's flex-1 already makes
  // definite — which is why this needs nothing from main itself, and cannot
  // affect any other page. Padding keeps it off the header and footer on short
  // windows, where centring alone would let them touch.
  return (
    <div className="flex min-h-full flex-col items-center justify-center py-[48px]">
      <h1 className="flex items-center gap-3 md:gap-4 font-display text-[32px] md:text-[48px] font-extrabold text-base-black dark:text-white mb-[16px]">
        <svg width="34" height="34" viewBox="0 0 64 64" fill="none" className="md:w-[52px] md:h-[52px] shrink-0">
          <circle cx="12" cy="32" r="9" fill="#0ea5c9" />
          <circle cx="32" cy="32" r="9" fill="currentColor" />
          <circle cx="52" cy="32" r="9" fill="currentColor" opacity="0.2" />
        </svg>
        Standby
      </h1>
      <p className="font-body text-[18px] text-gray-900 text-center max-w-[600px] mb-[24px]">
        {t('home.tagline')}
      </p>
      {!isAuthenticated && (
        <Link
          to={ROUTER_LINKS.SIGNUP}
          className="h-[52px] px-[32px] flex items-center bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] rounded-[32px] font-body text-[16px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
        >
          {t('home.getStarted')}
        </Link>
      )}
      {isAuthenticated && (
        <Link
          to={ROUTER_LINKS.DASHBOARD}
          className="h-[52px] px-[32px] flex items-center bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] rounded-[32px] font-body text-[16px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
        >
          {t('home.goToDashboard')}
        </Link>
      )}
    </div>
  );
};

export function MainRouter() {
  const { isAuthenticated } = useAuth();

  return (
    <Router>
      <div
        // 100svh, not min-h-screen. 100vh on a phone is the viewport with the
        // browser's address bar collapsed, which is taller than what you can
        // actually see — so a page told to fill it hangs below the fold, and
        // anything centred inside sits low rather than in the middle. svh is
        // the small viewport: the height that is always visible. The fallback
        // matters for older Safari, which ignores svh entirely.
        className="flex max-w-[1300px] relative mx-auto flex-col min-h-screen min-h-[100svh]"
        style={{ width: '90%' }}
      >
        <Header />
        {/* Stays a block. Making this a flex container narrowed every page in
            the app: the page roots are `max-w-[700px] mx-auto`, which as a
            block means "fill the width, cap at 700, centre" — but as a flex
            item, auto margins on the cross axis override align-items: stretch,
            so each page stopped stretching and shrank to its content instead.
            flex-1 still gives it the height between header and footer, which is
            all the landing page needs to centre against. */}
        <main className="flex-1">
        <Routes>
          <Route element={<AuthPageContainer />} path={APP_KEYS.ROUTER_KEYS.AUTH} />
          <Route element={<HomePage />} path={APP_KEYS.ROUTER_KEYS.ROOT} />
          <Route element={<PricingPage />} path={APP_KEYS.ROUTER_KEYS.PRICING} />
          <Route
            element={isAuthenticated ? <SettingsPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.SETTINGS}
          />
          <Route
            element={isAuthenticated ? <DashboardPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.DASHBOARD}
          />
          <Route element={<ReviewPage />} path={APP_KEYS.ROUTER_KEYS.REVIEW} />
          <Route
            element={isAuthenticated ? <ReviewAdminPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.REVIEW_ADMIN}
          />
          <Route
            element={isAuthenticated ? <TeamsPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.TEAMS}
          />
          <Route
            element={isAuthenticated ? <TeamDashboard /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.TEAM}
          />
          <Route
            element={isAuthenticated ? <TeamAdminPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.TEAM_ADMIN}
          />
          <Route element={<JoinTeamPage />} path={APP_KEYS.ROUTER_KEYS.TEAM_JOIN} />
          <Route element={<DocsPage />} path={APP_KEYS.ROUTER_KEYS.DOCS} />
          <Route element={<ContactPage />} path={APP_KEYS.ROUTER_KEYS.CONTACT} />
          <Route element={<TermsPage />} path={APP_KEYS.ROUTER_KEYS.TERMS} />
          <Route element={<PrivacyPage />} path={APP_KEYS.ROUTER_KEYS.PRIVACY} />
          <Route
            element={isAuthenticated ? <SiteAdminPage /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.ADMIN}
          />
          <Route
            element={isAuthenticated ? <SiteChangelog /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.ADMIN_CHANGELOG}
          />
          <Route
            element={isAuthenticated ? <CompanyDashboard /> : <AuthPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.COMPANY}
          />
          {/* Company-branded subsite routes */}
          <Route element={<CompanyBrandingProvider />} path={APP_KEYS.ROUTER_KEYS.COMPANY_SUBSITE}>
            <Route index element={<ReviewPage />} />
            <Route path="review/:slug" element={<ReviewPage />} />
            <Route
              path="review/:slug/admin"
              element={isAuthenticated ? <ReviewAdminPage /> : <AuthPageContainer />}
            />
          </Route>
          <Route element={<DeveloperPage />} path={APP_KEYS.ROUTER_KEYS.DEVELOPER} />
          <Route element={<OAuthAuthorizePage />} path={APP_KEYS.ROUTER_KEYS.OAUTH_AUTHORIZE} />
          <Route element={<QrRedirectPage />} path={APP_KEYS.ROUTER_KEYS.QR_CHECKIN} />
          <Route element={<InvitePage />} path={APP_KEYS.ROUTER_KEYS.INVITE} />
          <Route element={<MfaApprovePage />} path={APP_KEYS.ROUTER_KEYS.MFA_APPROVE} />
          <Route element={<LogoPreviewPage />} path="/logo-preview" />
          <Route path={APP_KEYS.ROUTER_KEYS.NOT_FOUND} element={<NotFound />} />
        </Routes>
        </main>
        <Footer />
        <CookieConsent />
        <EnableNotificationsPrompt />
      </div>
    </Router>
  );
}
